import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Grid,
  Avatar,
  Typography,
  Divider,
  InputAdornment,
  MenuItem,
  TextField,
  useMediaQuery,
  Skeleton,
  Stack,
  Button,
} from '@mui/material';
import ShabashWinnerCard from './ShabashWinnerCard';
import Light from '../../RnR/assets/light.gif';
import { useGetNominationsByYearQuery, useGetYearsQuery } from '../shabashApiSlice';
import CustomTextField from '../../../common/Form/TextField';
import { SearchOutlined } from '@mui/icons-material';
import ShabashWinnerDialog from './ShabashWinnerDialog';
import RnRCardsSkeleton from '../../RnR/RnRWinners/components/RnRCardsSkeleton';
import { useTheme } from '@emotion/react';
import linkwaylogo from '../../../assets/Linkway/LinkwayLogo.png';
import GNXTLogo from '../../../assets/GatewayLogo/G_NXTLogo.png';
import SmallShabashWinnerCard from './SmallShabashWinnerCard';
import NotFound from '../../../utils/NotFound';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Can } from '../../../context/Ability';
import { variants } from '../../../variants/variants';
import SwipeEdge from '../../../common/SwipeEdge';
import ShabashDrawer from './ShabashDrawer';

const ShabashWinners = () => {
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const urlElements = pathname.split('/');
  const drawerKey = urlElements[4];
  let drawerOpen = ['comment', 'rnr', 'shabash', 'resume', 'rnrNomination', 'shabashNomination'].includes(drawerKey);
  const { data: winnerData, isLoading, error } = useGetNominationsByYearQuery(selectedYear);
  const { data: years } = useGetYearsQuery();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500); // Adjust the delay (in milliseconds) as needed

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedWinner(null);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const filteredData = useMemo(() => {
    if (!winnerData || !winnerData.data) return {};

    const lowerCaseSearchText = debouncedSearchText.toLowerCase();
    const filtered = {};

    Object.keys(winnerData.data).forEach((month) => {
      const winners = winnerData.data[month].filter((winner) =>
        winner.name.toLowerCase().includes(lowerCaseSearchText)
      );
      if (winners.length > 0) {
        filtered[month] = winners;
      }
    });

    return filtered;
  }, [winnerData, debouncedSearchText]);

  if (error) return <p>Error loading winners</p>;

  return (
    <>
      {isLoading ? (
        isSm ? (
          <>
            <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
              <Skeleton height={'5vh'} variant="rectangular" sx={{ m: 2 }} width={'45%'} />
              <Skeleton height={'5vh'} variant="rectangular" sx={{ m: 2 }} width={'25%'} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '80vh',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(5px)',
              }}
            >
              <img src={GNXTLogo} style={{ height: 70, width: 150 }} />
              {/* <Avatar 
              variant={'rounded'} 
              src={GNXTLogo} sx={{ height: 100, width: 350 }} /> */}
              <Typography sx={{ mt: 2 }}>Loading...</Typography>
            </Box>
          </>
        ) : (
          <RnRCardsSkeleton />
        )
      ) : (
        <Box
          sx={{
            backgroundColor: '#fff',
            p: 2,
            border: 0.5,
            borderColor: 'divider',
            borderRadius: '8px',
            overflow: 'hidden',
            height: '100%',
          }}
        >
          <Box width={'100%'} display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={1}>
            <CustomTextField
              value={searchText}
              onChange={handleSearchChange}
              className="search"
              size="small"
              placeholder="Search"
              sx={{
                border: 1,
                fontSize: '14px',
                borderColor: 'divider',
                borderRadius: '8px',
                width: '100%',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Select Year"
              value={selectedYear}
              onChange={handleYearChange}
              variant="outlined"
              size="small"
              sx={{ minWidth: isSm ? 100 : 150 }}
            >
              {years?.data?.map((year) => (
                <MenuItem key={year} value={year}>
                  {year || Date.now().getFullYear()}
                </MenuItem>
              ))}
            </TextField>
            {!isSm && (
              <Can I={'create'} a={'RnR' || 'Shabash'}>
                <Box sx={{ mx: 2 }}>
                  <Button
                    disabled={drawerOpen}
                    variant="contained"
                    sx={{ fontWeight: 'bold', borderRadius: '8px', fontSize: variants.fontSize.TEXT }}
                    onClick={() => {
                      navigate('/home/shabash/winners/shabashNomination'); // Go to the shabashForm route
                    }}
                  >
                    Nominate
                  </Button>
                </Box>
              </Can>
            )}
          </Box>
          <Grid container>
            {!Object.keys(filteredData).length ? (
              <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={'100%'} flex={1}>
                <NotFound message={'No result found'} height={'60vh'} />
              </Box>
            ) : (
              <Stack
                sx={{
                  height: 'calc(100vh - 160px)',
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                  width: 1,
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              >
                {Object.keys(filteredData).map((month) => (
                  <>
                    <Box>
                      <Typography variant="h6" sx={{ fontSize: isSm ? '18px' : '20px' }} gutterBottom>
                        {month}
                      </Typography>
                      <Divider sx={{ mb: 1, mt: 1, width: '100%' }} />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: isSm ? 'column' : 'row',
                        flexWrap: 'wrap',
                        width: 1,
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                      }}
                    >
                      {filteredData[month].map((winner, i) =>
                        isSm ? (
                          <SmallShabashWinnerCard
                            key={i}
                            winner={winner}
                            setSelectedWinner={setSelectedWinner}
                            setOpen={setOpen}
                          />
                        ) : (
                          <ShabashWinnerCard
                            key={i}
                            winner={winner}
                            setSelectedWinner={setSelectedWinner}
                            setOpen={setOpen}
                          />
                        )
                      )}
                    </Box>
                  </>
                ))}
              </Stack>
            )}
          </Grid>
          {/* <Outlet/> */}
        </Box>
      )}
      {isSm && (
        <Can I={'create'} a={'RnR' || 'Shabash'}>
          <SwipeEdge
            height="70vh"
            title="Nominate for Shabash"
            content={
              <>
                <ShabashDrawer />
              </>
            }
          />
        </Can>
      )}
      <ShabashWinnerDialog onClose={handleCloseDialog} open={open} data={selectedWinner} />
    </>
  );
};

export default ShabashWinners;
